import { z } from 'zod';
import { ComponentKindSchema } from '../domain/application.js';
import { FindingStatusSchema, SeveritySchema } from '../domain/finding.js';
import { ApplicationSchema, ComponentSchema } from './application.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema } from './common.js';
/**
 * The surface map: what scans have observed of an application, laid over the components a
 * person declared it to have.
 *
 * Two kinds of knowledge meet here and are never blurred. *Observed* — hosts, endpoints and
 * findings — comes from scans and is evidence. *Declared* — components and which one calls which
 * — comes from a person and is a claim. The map shows where they agree, and where they do not:
 * surface no component accounts for, and components no scan has reached.
 *
 * Every path, host and parameter name here came from the application under test, so all of it is
 * untrusted text. It is displayed, never interpreted (§4).
 */
export const SURFACE_MAX_ENDPOINTS = 1500;
export const SURFACE_MAX_EXTERNAL_HOSTS = 100;
/** What an endpoint looks like it serves, from its content type and path. A label, not a fact. */
export const EndpointKindSchema = z.enum(['PAGE', 'API', 'STATIC', 'DOCUMENT', 'OTHER']);
/** Where an endpoint was seen: the crawl, or only as the location of a scanner alert. */
export const EndpointSourceSchema = z.enum(['CRAWL', 'ALERT']);
/**
 * One endpoint as one scan saw it.
 *
 * `path` is a template: numeric, UUID and hash-like segments are replaced by `{id}`, `{uuid}`
 * and `{hash}`, so `/basket/1` and `/basket/2` are one endpoint rather than a list that grows
 * with the data. The query string is dropped; only its parameter names are kept, because they
 * are inputs, and inputs are surface.
 */
export const ScanEndpointSchema = z.object({
    host: z.string().max(253),
    path: z.string().max(512),
    methods: z.array(z.string().max(10)).max(10),
    statuses: z.array(z.number().int().min(100).max(599)).max(10),
    kind: EndpointKindSchema,
    params: z.array(z.string().max(100)).max(30),
    sources: z.array(EndpointSourceSchema).max(2),
});
/** A host a crawl saw referenced but that is not in scope, so it was never scanned. */
export const ExternalHostSchema = z.object({
    host: z.string().max(253),
    references: z.number().int().nonnegative(),
});
/**
 * What one scan observed. Written once, when the scan is recorded, and never edited — a later
 * scan adds its own rather than changing this one, which is what lets the map say what changed.
 */
export const ScanSurfaceSchema = z.object({
    scanId: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    applicationId: IdSchema,
    observedAt: IsoDateTimeSchema,
    /** The approved hosts this scan was allowed to reach — what "in scope" meant for it. */
    scopeHosts: z.array(z.string().max(253)),
    endpoints: z.array(ScanEndpointSchema).max(SURFACE_MAX_ENDPOINTS),
    externalHosts: z.array(ExternalHostSchema).max(SURFACE_MAX_EXTERNAL_HOSTS),
    /** False when the scanner uploaded no crawl: the endpoints are then only alert locations. */
    crawlCaptured: z.boolean(),
    /** True when the scan saw more endpoints than are kept. The map says so rather than imply completeness. */
    truncated: z.boolean(),
});
/**
 * How an endpoint compares across scans of its host.
 *
 * `NEW` — first seen by the most recent scan of this host, and an earlier scan of it exists.
 * `GONE` — seen before, but the most recent scan of this host did not see it.
 * `STABLE` — otherwise, including when there is only one scan to judge by.
 */
export const EndpointChangeSchema = z.enum(['NEW', 'STABLE', 'GONE']);
/** A finding as the map needs it: enough to colour a node and link to the finding. */
export const SurfaceFindingSchema = z.object({
    id: IdSchema,
    engagementId: IdSchema,
    title: z.string().max(300),
    severity: SeveritySchema,
    status: FindingStatusSchema,
    host: z.string().max(253).optional(),
    path: z.string().max(512).optional(),
});
export const SurfaceEndpointSchema = ScanEndpointSchema.extend({
    firstSeenAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    change: EndpointChangeSchema,
    /** Open findings located at this endpoint. */
    findingIds: z.array(IdSchema),
});
/** Open findings by severity. "Open" is anything not decided FALSE_POSITIVE or FIXED. */
export const SeverityCountsSchema = z.record(SeveritySchema, z.number().int().nonnegative());
export const SurfaceComponentSchema = z.object({
    component: ComponentSchema,
    /** Hosts this component was observed on, whether or not it declares them. */
    observedHosts: z.array(z.string().max(253)),
    endpoints: z.array(SurfaceEndpointSchema),
    findings: z.array(SurfaceFindingSchema),
    open: SeverityCountsSchema,
});
export const SurfaceHostSchema = z.object({
    host: z.string().max(253),
    endpointCount: z.number().int().nonnegative(),
    lastSeenAt: IsoDateTimeSchema,
    /** Components with endpoints observed on this host. */
    componentIds: z.array(IdSchema),
});
/**
 * A component the map proposes for surface no declared component accounts for — one host and
 * first path segment at a time. Only a proposal: it exists in the application once a person adds it.
 */
export const ComponentSuggestionSchema = z.object({
    name: z.string().max(80),
    kind: ComponentKindSchema,
    host: z.string().max(253),
    pathPrefix: z.string().max(512).optional(),
    endpointCount: z.number().int().positive(),
});
export const SurfaceScanSchema = z.object({
    scanId: IdSchema,
    engagementId: IdSchema,
    observedAt: IsoDateTimeSchema,
    endpointCount: z.number().int().nonnegative(),
    crawlCaptured: z.boolean(),
    truncated: z.boolean(),
});
/** GET /v1/applications/{id}/surface */
export const SurfaceMapSchema = z.object({
    application: ApplicationSchema,
    generatedAt: IsoDateTimeSchema,
    /** The scans the map is built from, newest first. Empty until a scan of this application finishes. */
    scans: z.array(SurfaceScanSchema),
    hosts: z.array(SurfaceHostSchema),
    components: z.array(SurfaceComponentSchema),
    unmapped: z.object({
        endpoints: z.array(SurfaceEndpointSchema),
        findings: z.array(SurfaceFindingSchema),
        open: SeverityCountsSchema,
    }),
    suggestions: z.array(ComponentSuggestionSchema),
    externalHosts: z.array(ExternalHostSchema),
});
//# sourceMappingURL=surface.js.map